import { useGT } from 'gt-react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { TableExportActions } from './TableExportActions';

// Mirrors the backend render_table inputSchema. Local (not shared via
// @erp/rpc) because it's tiny and this is the only consumer.
const tableArgsSchema = z.object({
  title: z.string().optional(),
  columns: z.array(z.string()).min(1),
  rows: z.array(z.array(z.string())).min(1),
});

interface TableToolResultProps {
  /** The `render_table` tool-call args: `{ title?, columns, rows }`. */
  args: Record<string, unknown>;
}

/**
 * Inline generative-UI card: renders the AI's `render_table` spec as a compact,
 * horizontally scrollable table (Tailwind + shadcn Card only). Rows are keyed
 * by index and render whatever cells they contain, so a row longer or shorter
 * than the header set degrades gracefully.
 */
export function TableToolResult({ args }: TableToolResultProps) {
  const gt = useGT();
  const parsed = tableArgsSchema.safeParse(args);
  const { title, columns, rows } = parsed.success
    ? parsed.data
    : { title: undefined, columns: [] as string[], rows: [] as string[][] };

  // A schema-valid but content-empty spec (e.g. rows: [['']]) would render a
  // degenerate table of blanks — fall back instead, mirroring the backend.
  const hasContent =
    parsed.success &&
    parsed.data.rows.some((row) => row.some((cell) => cell.trim() !== ''));

  if (!parsed.success || !hasContent) {
    return (
      <Card>
        <p className="text-muted-foreground text-sm">
          {gt('Unable to render table.', {
            context:
              'shown when an AI-generated table spec is invalid or has no data',
          })}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        {title ? (
          <div className="text-card-foreground text-sm font-medium">
            {title}
          </div>
        ) : (
          <span />
        )}
        <TableExportActions columns={columns} rows={rows} title={title} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              {columns.map((col, colIndex) => (
                <th
                  key={colIndex}
                  className="text-muted-foreground px-2 py-1.5 text-left text-xs font-medium"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-border/50 border-b last:border-0"
              >
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="text-card-foreground px-2 py-1.5 tabular-nums"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
